import type { LocaleConfig } from '@algolia-search/types';
import { readonly, ref, useContext } from '@nuxtjs/composition-api';
import type { InsightsClient } from 'search-insights';
import insights from 'search-insights';

import { Logger } from '~/helpers/logger';
import useUser from '~/modules/customer/composables/useUser';

const insightsClientsCache: Record<string, InsightsClient> = {};

export const useAlgoliaInsightsClient = () => {
  const {
    app: { i18n },
    $algoliaSearch
  } = useContext();
  const { user, load: loadUser } = useUser();
  const localeCode = i18n.localeProperties.code;
  const config = $algoliaSearch.localeConfigs?.[localeCode] ?? {
    appId: $algoliaSearch.defaultAppId,
    apiKey: $algoliaSearch.defaultApiKey,
    analyticsApiKey: $algoliaSearch.defaultAnalyticsApiKey
  };

  const insightsClient = ref<InsightsClient | null>(null);
  const clientConfig = ref<LocaleConfig | null>(null);
  const isLoading = ref<boolean>(false);
  const error = ref<Error | null>(null);

  const initialize = async () => {
    if (insightsClient.value) return;

    try {
      isLoading.value = true;

      if (!user.value) {
        await loadUser();
      }

      if (!insightsClientsCache[localeCode]) {
        insights('init', {
          appId: config.appId,
          apiKey: config.apiKey,
          ...(user.value?.nosto_customer_reference && {
            userToken: user.value.nosto_customer_reference
          })
        });
        insightsClientsCache[localeCode] = insights;
      }

      insightsClient.value = insightsClientsCache[localeCode];
      clientConfig.value = config;
    } catch (err) {
      Logger.error('Error initialising insights client', err);
      error.value = err as Error;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    insightsClient: readonly(insightsClient),
    clientConfig: readonly(clientConfig),
    isLoading: readonly(isLoading),
    error: readonly(error),
    initialize
  };
};
